import React from "react";

import {
  Box,
  Grid,
  Link,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { MailOutline as MailIcon } from "@mui/icons-material";

import { colors } from "theme";

interface GlobalPartner {
  countryName: string;
  companyName: string;
  website: string;
  email: string;
}

const AMERICA_PARTNERS: GlobalPartner[] = [
  {
    countryName: "Mexico",
    companyName: "Abalat S.A. de C.V.",
    website: "https://abalat.mx/",
    email: "dabad@abalat.com.mx",
  },
];

const AFRICA_PARTNERS: GlobalPartner[] = [
  {
    countryName: "South Africa",
    companyName: "Diagnostech",
    website: "https://diagnostech.co.za/",
    email: "michelle@diagnostech.co.za",
  },
];

const OCEANIA_PARTNERS: GlobalPartner[] = [
  {
    countryName: "Australia",
    companyName: "Integrated Sciences",
    website: "https://www.integratedsci.com.au/",
    email: "hfraval@integratedsci.com.au",
  },
  {
    countryName: "China",
    companyName: "Biostar Technology Limited",
    website: "https://www.bio-star.cn/",
    email: "http://marketing@bio-star.cn/",
  },
  {
    countryName:
      "Gulf Cooperation Council (GCC), Egypt, Iraq, Lebanon, Pakistan",
    companyName: "Alliance Global",
    website: "https://www.agbl.net/",
    email: "genomics@agbl.net",
  },
  {
    countryName: "India",
    companyName: "Premas Life Sciences Pvt. Ltd.",
    website: "https://www.premaslifesciences.com/",
    email: "info@neoscience.com.my",
  },
  {
    countryName: "Israel",
    companyName: "Danyel Biotech Ltd.",
    website: "https://www.danyel.co.il/",
    email: "genomics@danyel.co.il",
  },
  {
    countryName: "Japan",
    companyName: "Biostream",
    website: "https://www.biostream.co.jp/",
    email: "takeshi.iwabuchi@biostream.co.jp",
  },
  {
    countryName: "Korea",
    companyName: "Leaninbio",
    website: "http://leaninbio.co.kr",
    email: "alicekjlee@leaninbio.co.kr",
  },
  {
    countryName: "Taiwan",
    companyName: "Feng Chi Biotech Corp.",
    website: "https://www.fcbiotech.com.tw/",
    email: "matt.chang@fcbiotech.com.tw",
  },
  {
    countryName: "Thailand, Cambodia, Laos",
    companyName: "Bio-Active",
    website: "https://www.bio-active.co.th/",
    email: "suwan@bio-active.co.th",
  },
  {
    countryName: "Turkey",
    companyName: "Gen Era",
    website: "http://gen-era.com.tr/",
    email: "info@gen-era.com.tr",
  },
];

const EUROPE_PARTNERS: GlobalPartner[] = [
  {
    countryName: "Bulgaria, Macedonia, Romania, Serbia",
    companyName: "ELTA 90 M Ltd.",
    website: "http://www.elta90.com/",
    email: "borislav.a@elta90.eu",
  },
  {
    countryName: "Czech Republic, Slovakia, Hungary",
    companyName: "PentaGen s.r.o",
    website: "https://www.pentagen.cz/",
    email: "phorak@pentagen.cz",
  },
  {
    countryName: "Greece",
    companyName: "Varelas S.A.",
    website: "http://www.varelas.gr/",
    email: "info@varelas.gr",
  },
  {
    countryName: "Poland",
    companyName: "Analityk Genetyka",
    website: "https://www.analitykgenetyka.pl/",
    email: "biuro@analitykgenetyka.pl",
  },
  {
    countryName: "Slovenia, Croatia",
    companyName: "Kemomed d.o.o.",
    website: "http://kemomed.si/",
    email: "info@kemomed.si",
  },
  {
    countryName: "Spain, Portugal",
    companyName: "Diagnostica Longwood",
    website: "https://dlongwood.com/",
    email: "mangel.olivares@dlongwood.com",
  },
];

const GlobalPartnerComp: React.FC<GlobalPartner> = ({
  countryName,
  companyName,
  website,
  email,
}) => (
  <Box className="global-partner">
    <Typography variant="h6" component="h4">
      {countryName}
    </Typography>
    <Box
      display="flex"
      justifyContent="space-between"
      columnGap={1}
      p={1}
      pl={0}
      pb="7px"
      borderBottom={`1px solid ${colors.grey[600]}`}
    >
      <Link href={website} target="_blank" color="secondary">
        {companyName}
      </Link>
      <Link href={`mailto:${email}`} color="primary" target="_blank">
        <Box display="flex" height={1} alignItems="center">
          <MailIcon />
        </Box>
      </Link>
    </Box>
  </Box>
);

const RegionPartners: React.FC<{
  regionName: string;
  partners: GlobalPartner[];
}> = ({ regionName, partners }) => (
  <Box display="flex" flexDirection="column">
    <Typography variant="h5" component="h3">
      {regionName}
    </Typography>
    <Box
      mt={2}
      component="ul"
      gap="16px"
      display="flex"
      flexDirection="column"
    >
      {partners.map((partner) => (
        <Box key={partner.email} component="li">
          <GlobalPartnerComp {...partner} />
        </Box>
      ))}
    </Box>
  </Box>
);

export const GlobalPartners = () => {
  const xs = useMediaQuery((theme: Theme) => theme.breakpoints.only("xs"));

  return (
    <Grid
      container
      spacing={3}
      justifyContent="center"
      component="ul"
      className="global-partners"
    >
      <Grid item xs={12} sm={4} component="li" spacing={3}>
        <Box display="flex" flexDirection="column" gap={xs ? "24px" : "16px"}>
          <RegionPartners regionName="Americas" partners={AMERICA_PARTNERS} />
          <RegionPartners regionName="Africa" partners={AFRICA_PARTNERS} />
        </Box>
      </Grid>
      <Grid item xs={12} sm={4} component="li">
        <RegionPartners regionName="Asia/Oceania" partners={OCEANIA_PARTNERS} />
      </Grid>
      <Grid item xs={12} sm={4} component="li">
        <RegionPartners regionName="Europe" partners={EUROPE_PARTNERS} />
      </Grid>
    </Grid>
  );
};
